import { ElementType } from 'react';

import { Button as MuiButton, ButtonBaseProps, ButtonProps as MuiButtonProps, styled } from '@mui/material';

import { generateClasses } from '../../utils/helper';
import { Props as VMProps } from './vm';

export const buttonClasses = {
  ...generateClasses('Button', [
    'root',
    'contained',
    'outlined',
    'text',
    'sizeXLarge',
    'sizeLarge',
    'sizeMedium',
    'sizeSmall',
    'sizeXSmall',
    'colorPrimary',
    'colorSuccess',
    'colorInfo',
    'colorWarning',
    'colorError',
    'startIcon',
    'endIcon',
    'edgeStart',
    'edgeEnd',
    'focusVisible',
    'disabled',
    'loading',
    'spinner',
  ]),
};

export type ButtonProps<C extends ElementType<any>> = VMProps &
  DistributiveOmit<MuiButtonProps, 'color' | 'onClick'> & {
    component?: C;
    /** @default 'primary' */
    color?: 'primary' | 'success' | 'info' | 'error' | 'warning';
    edge?: 'start' | 'end';
    iconOnly?: boolean;
    classes?: Partial<typeof buttonClasses>;
  };

export type Props<C extends ElementType<any> = 'button'> = ButtonBaseProps<C, ButtonProps<C>>;

export const ButtonRoot = styled(MuiButton, {
  shouldForwardProp: (prop) => !['isLoading', 'iconOnly'].includes(prop as string),
})<DistributiveOmit<Props, 'color'> & Required<Pick<Props, 'color'>>>(({
  theme,
  disabled,
  isLoading,
  color,
  variant,
  size,
}) => {
  let cursor = 'pointer';

  if (disabled) cursor = 'not-allowed';
  if (isLoading) cursor = 'progress';

  let backgroundColor,
    foregroundColor,
    borderColor,
    opacity,
    hoverBackgroundColor,
    hoverColor,
    hoverBorderColor,
    focusBackgroundColor,
    focusColor,
    focusBorderColor;

  switch (variant) {
    case 'outlined':
      backgroundColor = 'transparent';
      foregroundColor = theme.palette[color][600];
      borderColor = theme.palette[color][600];
      hoverBackgroundColor = theme.palette[color][50];
      focusBackgroundColor = theme.palette[color][100];
      if (disabled && !isLoading) {
        backgroundColor = theme.palette.grey[50];
        foregroundColor = theme.palette.grey.contrastColors[50];
        borderColor = theme.palette.grey.contrastColors[50];
        opacity = 0.4;
      }
      break;
    case 'text':
      backgroundColor = 'transparent';
      foregroundColor = theme.palette[color][600];
      hoverBackgroundColor = theme.palette[color][50];
      focusBackgroundColor = theme.palette[color][100];
      if (disabled && !isLoading) {
        backgroundColor = 'transparent';
        foregroundColor = theme.palette.grey.contrastColors[300];
        opacity = 0.4;
      }
      break;
    case 'contained':
    default:
      backgroundColor = theme.palette[color][600];
      foregroundColor = theme.palette[color].contrastColors[600];
      hoverBackgroundColor = theme.palette[color][500];
      hoverColor = theme.palette[color].contrastColors[500];
      focusBackgroundColor = theme.palette[color][700];
      focusColor = theme.palette[color].contrastColors[700];
      if (disabled && !isLoading) {
        backgroundColor = theme.palette.grey[300];
        foregroundColor = theme.palette.grey.contrastColors[300];
        opacity = 0.5;
      }
      break;
  }

  let gap, fontSize, lineHeight, borderRadius, padding, minHeight, edgeMarginHorizontal, iconSize;

  switch (size) {
    case 'xLarge':
      gap = theme.spacing(3);
      minHeight = 56;
      fontSize = theme.typography.pxToRem(18);
      lineHeight = 1.33;
      borderRadius = 10;
      padding = `calc(${theme.spacing(4)} - 1px)`;
      edgeMarginHorizontal = `calc(-1 * (${theme.spacing(4)} - 1px))`;
      iconSize = theme.typography.pxToRem(24);
      break;
    case 'large':
      gap = theme.spacing(2);
      minHeight = 48;
      fontSize = theme.typography.pxToRem(16);
      lineHeight = 1.5;
      borderRadius = 8;
      padding = `calc(${theme.spacing(3)} - 1px)`;
      edgeMarginHorizontal = `calc(-1 * (${theme.spacing(3)} - 1px))`;
      iconSize = theme.typography.pxToRem(24);
      break;
    case 'small':
      gap = theme.spacing(1);
      minHeight = 32;
      fontSize = theme.typography.pxToRem(12);
      lineHeight = 1.33;
      borderRadius = 4;
      padding = `calc(${theme.spacing(2)} - 1px)`;
      edgeMarginHorizontal = `calc(-1 * (${theme.spacing(2)} - 1px))`;
      iconSize = theme.typography.pxToRem(16);
      break;
    case 'xSmall':
      gap = theme.spacing(1);
      minHeight = 28;
      fontSize = theme.typography.pxToRem(10);
      lineHeight = 1.2;
      borderRadius = 4;
      padding = `calc(${theme.spacing(2)} - 1px)`;
      edgeMarginHorizontal = `calc(-1 * (${theme.spacing(2)} - 1px))`;
      iconSize = theme.typography.pxToRem(12);
      break;
    case 'medium':
    default:
      gap = theme.spacing(2);
      minHeight = 40;
      fontSize = theme.typography.pxToRem(14);
      lineHeight = 1.14;
      borderRadius = 6;
      padding = `calc(${theme.spacing(3)} - 1px)`;
      edgeMarginHorizontal = `calc(-1 * (${theme.spacing(3)} - 1px))`;
      iconSize = theme.typography.pxToRem(16);
      break;
  }

  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 'unset',
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'none',
    border: '1px solid transparent',

    cursor,

    backgroundColor,
    color: foregroundColor,
    borderColor,
    opacity,

    gap,
    fontSize,
    lineHeight,
    borderRadius,
    padding,
    minHeight,

    '&:hover': {
      backgroundColor: hoverBackgroundColor,
      color: hoverColor,
      borderColor: hoverBorderColor,
    },

    '&:focus': {
      backgroundColor: focusBackgroundColor,
      color: focusColor,
      borderColor: focusBorderColor,
    },

    [`&.${buttonClasses.edgeStart}`]: {
      marginLeft: edgeMarginHorizontal,
    },
    [`&.${buttonClasses.edgeEnd}`]: {
      marginRight: edgeMarginHorizontal,
    },

    [`& .${buttonClasses.startIcon}, & .${buttonClasses.endIcon}`]: {
      margin: 0,
      '& > *': {
        width: iconSize,
        height: iconSize,
      },
    },
  };
});
